<template>
  <a-select
    show-search
    :mode="multiply ? 'multiple' : 'default'"
    v-model="templateProxy"
    :autoClearSearchValue="false"
    :allow-clear="true"
    :loading="loadingTemplates"
    :filter-option="false"
    :disabled="disabled"
    :placeholder="loadingTemplates ? 'Loading...' : 'Start typing to search Template'"
    @search="searchTemplates"
    @focus="focusTemplates"
    @blur="blurTemplates"
  >
    <a-select-option v-for="template in templates" :key="template.id" :value="template.id">
      {{ template.name }} </a-select-option
    ><template slot="dropdownRender" slot-scope="menu">
      <a-spin tip="Loading..." :spinning="loadingTemplates">
        <v-nodes :vnodes="menu" />
      </a-spin>
    </template>
  </a-select>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import VNodes from '@/components/ui/v-nodes.vue'
import { notification, Select, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: documentActions } = createNamespacedHelpers('document')

export default {
  components: {
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-spin': Spin,
    VNodes
  },
  props: {
    value: {
      type: Number || Array,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiply: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingTemplates: false,
      template: [],
      templates: [],

      searchQuery: ''
    }
  },
  watch: {
    searchQuery: {
      handler() {
        this.fetchTemplates()
      },
      immediate: true
    },
    value: {
      handler(newVal) {
        const alreadyExist = this.multiply
          ? (newVal ?? []).every(template => this.templates.find(item => item.id === template.id))
          : this.templates.find(item => item.id === newVal)

        const needFetch = this.multiply ? newVal?.length : !!newVal

        if (newVal && !alreadyExist && needFetch) {
          this.fetchTemplate()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    templateProxy: {
      get() {
        if (Array.isArray(this.value)) {
          return this.value.map(item => item.id)
        }
        return this.value ? (this.value?.id ?? this.value) : undefined
      },
      set(value) {
        if (value === undefined) {
          this.searchQuery = ''

          this.$emit('update', undefined)

          return
        }

        this.template = this.multiply
          ? this.templates.filter(item => value.includes(item.id))
          : this.templates.find(item => item.id === value)

        this.$emit('update', this.template)
      }
    }
  },
  methods: {
    ...documentActions(['getAssociateTemplates']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    focusTemplates() {
      this.searchQuery = ''
    },

    blurTemplates() {
      this.searchQuery = ''
    },

    searchTemplates: debounce(function (value) {
      this.searchQuery = value
    }, 1000),

    async fetchTemplates() {
      this.loadingTemplates = true
      try {
        const data = await this.getAssociateTemplates({
          filters: {
            name: this.searchQuery
          },
          page: 1,
          size: 10,
          cancelPreviousRequests: false
        })

        if (this.template.length) {
          const templateIds = new Set(this.template.map(item => item.id))

          this.templates = [...this.template, ...data.items.filter(item => !templateIds.has(item.id))]
        } else {
          this.templates = [...data.items]
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loadingTemplates = false
      }
    },

    async fetchTemplate() {
      try {
        const data = await this.getAssociateTemplates({
          filters: {
            id: this.multiply || Array.isArray(this.value) ? this.value : [this.value]
          },
          size: 1000,
          cancelPreviousRequests: false
        })

        this.template = data.items

        if (this.template.length) {
          const templateIds = new Set(this.template.map(item => item.id))

          this.templates = [...this.template, ...this.templates.filter(item => !templateIds.has(item.id))]
        }

        this.$emit('update', this.multiply ? this.template : this.template?.[0])
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
        this.template = []
        this.$emit('update', this.multiply ? this.template : undefined)
      }
    }
  }
}
</script>

<style scoped></style>
